import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
interface SampleDealBriefProps {
  onBuyerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function SampleDealBrief({
  onBuyerContactOpen
}: SampleDealBriefProps) {
  return <main className="bg-deal-black pt-20" data-id="element-807">
      {/* Hero / Main Content Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20" data-id="element-808">
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-809" />
        <div className="absolute inset-0 bg-gradient-to-b from-deal-black/20 via-transparent to-deal-black" data-id="element-810" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full" data-id="element-811">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-812">
            {/* Left Column: Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-813">
              <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-8 border border-deal-green/30 px-4 py-1.5" data-id="element-814">
                For Buyers
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-6" data-id="element-815">
                Sample Deal Brief
              </h1>
              <p className="text-lg text-white/50 leading-relaxed font-light mb-12" data-id="element-816">
                See exactly what you'll receive with each qualified seller
                introduction. This is the format delivered with every deal,
                designed for fast IC-level screening.
              </p>

              {/* Checklist Card */}
              <div className="bg-[#0d0d0d] border border-white/[0.06] p-8 md:p-10 rounded-xl" data-id="element-817">
                <h3 className="text-xl font-bold text-white mb-6" data-id="element-818">
                  What's Inside the Brief
                </h3>
                <ul className="space-y-4" data-id="element-819">
                  {['One-page screening summary with key details at a glance', 'Owner motivation, timeline, and transition posture', 'Revenue and EBITDA ranges with margin context', 'Customer concentration snapshot (top customer %)', 'Key risks, flags, and honest assessment', 'Next-step recommendation (A / B / C grading)', 'Full call notes and recording access'].map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-820">
                      <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-821">
                        <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-822" />
                      </div>
                      <span className="text-white/70 text-sm leading-relaxed" data-id="element-823">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>

            {/* Right Column: Form */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-824">
              <div className="bg-[#111] border border-white/10 p-8 md:p-10 rounded-xl shadow-2xl relative overflow-hidden" data-id="element-825">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-deal-green to-transparent" data-id="element-826" />

                <h2 className="text-2xl font-bold text-white mb-2" data-id="element-827">
                  Get the Sample Brief
                </h2>
                <p className="text-white/50 text-sm mb-8" data-id="element-828">
                  See the exact format before you commit to anything.
                </p>

                <form className="space-y-5" onSubmit={e => e.preventDefault()} data-id="element-829">
                  <div data-id="element-830">
                    <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-831">
                      Full Name
                    </label>
                    <input type="text" className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors" placeholder="John Doe" data-id="element-832" />
                  </div>
                  <div data-id="element-833">
                    <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-834">
                      Firm Name
                    </label>
                    <input type="text" className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors" placeholder="Acme Capital" data-id="element-835" />
                  </div>
                  <div data-id="element-836">
                    <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-837">
                      Email Address
                    </label>
                    <input type="email" className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors" placeholder="john@example.com" data-id="element-838" />
                  </div>
                  <div data-id="element-839">
                    <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-840">
                      Buy Box Description{' '}
                      <span className="text-white/20 normal-case tracking-normal ml-1" data-id="element-841">
                        (Optional)
                      </span>
                    </label>
                    <textarea rows={3} className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors resize-none" placeholder="$5-10M EBITDA, healthcare services, Southeast US" data-id="element-842" />
                  </div>

                  <button type="submit" className="w-full bg-deal-green text-black font-bold uppercase tracking-widest py-4 rounded-lg hover:bg-deal-green-dark transition-colors mt-2 flex items-center justify-center gap-2" data-id="element-843">
                    Get the Sample Deal Brief
                    <ArrowRightIcon className="w-4 h-4" data-id="element-844" />
                  </button>

                  <p className="text-center text-white/30 text-xs mt-4" data-id="element-845">
                    Instant access. No commitment required.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Band */}
      <section className="py-20 bg-[#0d0d0d] border-t border-white/[0.06]" data-id="element-846">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-847">
          <motion.div {...fadeInUp} data-id="element-848">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-849">
              Ready to go beyond the sample?
            </h2>
            <p className="text-lg text-white/50 mb-10 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-850">
              The sample brief shows you the format. The pilot delivers 30–45 of
              them — each representing a real, qualified business owner in your
              buy box.
            </p>
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 inline-flex items-center gap-2" data-id="element-851">
              Start a 90-Day Pilot
              <ArrowRightIcon className="w-4 h-4" data-id="element-852" />
            </button>
          </motion.div>
        </div>
      </section>
    </main>;
}